import type { QuotationItem } from '../../../shared/api/quotations.api'
import { useColumnResize } from '../hooks/useColumnResize'

const MATRIX_LABELS: Record<string, string> = {
  stock: 'Склад',
  china: 'Китай',
  rf: 'РФ',
  manuf: 'Производители',
  other: 'Стоки',
}

const STATUS_META: Record<string, { label: string; tone: string }> = {
  ORDERED: { label: 'Счёт', tone: 'bg-emerald-100 text-emerald-700' },
  REJECTED_BY_CUSTOMER: { label: 'Отказ клиента', tone: 'bg-red-100 text-red-700' },
}

// Колонки с изменяемой шириной (между фикс-колонками «чекбокс» и «№» слева).
// Колонка «Заметка» — между «Поставщик» и «Кол-во», показывает заметку квоты.
type ColKey =
  | 'partNumber'
  | 'brand'
  | 'supplier'
  | 'note'
  | 'quantity'
  | 'cost'
  | 'markup'
  | 'price'
  | 'sum'
  | 'kpNote'
  | 'status'

const COLUMNS: { key: ColKey; label: string; align: 'left' | 'right' }[] = [
  { key: 'partNumber', label: 'Артикул', align: 'left' },
  { key: 'brand', label: 'Бренд', align: 'left' },
  { key: 'supplier', label: 'Поставщик', align: 'left' },
  { key: 'note', label: 'Заметка', align: 'left' },
  { key: 'quantity', label: 'Кол-во', align: 'right' },
  { key: 'cost', label: 'Себест.', align: 'right' },
  { key: 'markup', label: 'Наценка', align: 'right' },
  { key: 'price', label: 'Цена', align: 'right' },
  { key: 'sum', label: 'Сумма', align: 'right' },
  { key: 'kpNote', label: 'Примечание', align: 'left' },
  { key: 'status', label: 'Статус', align: 'left' },
]

// Текстовые столбцы переносят содержимое по словам при сужении (высота строки
// растёт, текст влезает целиком). Числовые остаются в одну строку — перенос
// числа выглядел бы как разрыв значения.
const WRAP_KEYS = new Set<ColKey>(['partNumber', 'brand', 'supplier', 'note', 'kpNote'])

const DEFAULT_WIDTHS: Record<ColKey, number> = {
  partNumber: 160,
  brand: 110,
  supplier: 150,
  note: 190,
  quantity: 74,
  cost: 96,
  markup: 84,
  price: 104,
  sum: 120,
  kpNote: 200,
  status: 120,
}

interface Props {
  items: QuotationItem[]
  totalSum: number
  selected: Set<string>
  allVisibleSelected: boolean
  onToggleAll: () => void
  onToggleOne: (id: string) => void
  statusById: Map<string, string>
  noteById: Map<string, string | null>
}

export function KPDetailsTable({
  items,
  totalSum,
  selected,
  allVisibleSelected,
  onToggleAll,
  onToggleOne,
  statusById,
  noteById,
}: Props) {
  const { widths, startResize } = useColumnResize<ColKey>(DEFAULT_WIDTHS)

  // Итоги по видимым позициям: общая себестоимость и средневзвешенная наценка
  // (сумма продажи / сумма себестоимости) — менеджер сразу видит, сколько
  // «наварено» по КП целиком, а не по строкам.
  const totalCost = items.reduce((acc, it) => acc + it.cost * it.quantity, 0)
  const avgMarkup = totalCost > 0 ? totalSum / totalCost : null

  // Фикс-колонки слева: чекбокс + №. Их ширина не меняется.
  const CHECK_W = 36
  const NUM_W = 40
  const tableWidth = CHECK_W + NUM_W + COLUMNS.reduce((a, c) => a + widths[c.key], 0)

  const cellValue = (it: QuotationItem, key: ColKey): string => {
    switch (key) {
      case 'partNumber':
        return it.partNumber
      case 'brand':
        return it.brand ?? '—'
      case 'supplier':
        return it.supplierName ?? MATRIX_LABELS[it.matrix] ?? it.matrix
      case 'note':
        return noteById.get(it.itemId) || '—'
      case 'kpNote':
        return it.note || '—'
      case 'quantity':
        return String(it.quantity)
      case 'cost':
        return it.cost.toFixed(2)
      case 'markup':
        return `×${it.markup}`
      case 'price':
        return it.priceWithMarkup.toFixed(2)
      case 'sum':
        return (it.priceWithMarkup * it.quantity).toFixed(2)
      default:
        return ''
    }
  }

  const cellClassFor = (key: ColKey): string => {
    const align = COLUMNS.find((c) => c.key === key)!.align === 'right' ? 'text-right' : 'text-left'
    const mono = ['cost', 'markup', 'price', 'sum'].includes(key) ? 'font-mono' : ''
    const tone =
      key === 'partNumber'
        ? 'font-mono'
        : key === 'brand' || key === 'supplier'
          ? 'text-gray-500'
          : key === 'cost'
            ? 'text-gray-400'
            : key === 'price'
              ? 'font-medium'
              : ''
    return `px-2 py-1.5 ${align} ${mono} ${tone}`.trim()
  }

  return (
    <div className="flex-1 overflow-auto">
      <table className="text-xs" style={{ tableLayout: 'fixed', width: tableWidth }}>
        <colgroup>
          <col style={{ width: CHECK_W }} />
          <col style={{ width: NUM_W }} />
          {COLUMNS.map((c) => (
            <col key={c.key} style={{ width: widths[c.key] }} />
          ))}
        </colgroup>
        <thead className="sticky top-0 bg-gray-50 border-b border-gray-200">
          <tr>
            <th className="px-2 py-2 text-center">
              <input
                type="checkbox"
                checked={allVisibleSelected}
                onChange={onToggleAll}
                className="accent-brand-600"
              />
            </th>
            <th className="px-2 py-2 text-left font-medium text-gray-500">№</th>
            {COLUMNS.map((c) => (
              <th
                key={c.key}
                className={`relative px-2 py-2 font-medium text-gray-500 overflow-hidden whitespace-nowrap ${
                  c.align === 'right' ? 'text-right' : 'text-left'
                }`}
              >
                <span className="block overflow-hidden text-ellipsis pr-1.5">{c.label}</span>
                <div
                  onMouseDown={(e) => startResize(c.key, e)}
                  className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize group"
                >
                  <div className="absolute right-0 top-1 bottom-1 w-px bg-gray-200 group-hover:bg-brand-400 group-hover:w-0.5 transition-colors" />
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map((it, idx) => {
            const status = statusById.get(it.itemId) ?? ''
            const meta = STATUS_META[status]
            const isSelected = selected.has(it.itemId)
            return (
              <tr
                key={it.itemId}
                className={`border-b border-gray-50 last:border-0 ${
                  isSelected ? 'bg-brand-50/40' : ''
                }`}
              >
                <td className="px-2 py-1.5 text-center align-top">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => onToggleOne(it.itemId)}
                    className="accent-brand-600"
                  />
                </td>
                <td className="px-2 py-1.5 text-gray-400 align-top">{idx + 1}</td>
                {COLUMNS.map((c) =>
                  c.key === 'status' ? (
                    <td key={c.key} className="px-2 py-1.5 align-top">
                      {meta ? (
                        <span
                          className={`inline-block px-2 py-0.5 rounded whitespace-nowrap ${meta.tone}`}
                        >
                          {meta.label}
                        </span>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                  ) : (
                    <td key={c.key} className={`${cellClassFor(c.key)} align-top`}>
                      {WRAP_KEYS.has(c.key) ? (
                        <div className="whitespace-normal break-words">{cellValue(it, c.key)}</div>
                      ) : (
                        <div className="truncate" title={cellValue(it, c.key)}>
                          {cellValue(it, c.key)}
                        </div>
                      )}
                    </td>
                  ),
                )}
              </tr>
            )
          })}
          {items.length === 0 && (
            <tr>
              <td colSpan={COLUMNS.length + 2} className="px-2 py-6 text-center text-gray-400">
                Под фильтры ничего не попало
              </td>
            </tr>
          )}
        </tbody>
        <tfoot>
          <tr className="border-t border-gray-200 bg-gray-50">
            {/* чекбокс + № + Артикул…Кол-во = 2 фикс + 5 колонок */}
            <td colSpan={7} className="px-2 py-1.5 text-right font-medium">
              Итого:
            </td>
            {/* Под «Себест.» — общая себестоимость, тем же серым что и строки */}
            <td
              className="px-2 py-1.5 text-right font-mono text-gray-400"
              title="Общая себестоимость по видимым позициям"
            >
              {totalCost.toLocaleString('ru-RU', { maximumFractionDigits: 2 })} ₽
            </td>
            {/* Под «Наценка» — средневзвешенная: сумма продажи / себестоимость */}
            <td
              className="px-2 py-1.5 text-right font-mono text-gray-500"
              title="Средняя наценка по КП (сумма / себестоимость)"
            >
              {avgMarkup !== null ? `×${avgMarkup.toFixed(2)}` : '—'}
            </td>
            <td></td>
            <td className="px-2 py-1.5 text-right font-mono font-medium">
              {totalSum.toLocaleString('ru-RU', { maximumFractionDigits: 2 })} ₽
            </td>
            {/* Примечание + Статус — пустые под «Итого» */}
            <td colSpan={2}></td>
          </tr>
        </tfoot>
      </table>
    </div>
  )
}
